import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Avatar
} from '@mui/material';
import { KeyboardArrowDownIcon } from 'icons/materialIcons';
import { useAuth } from 'contexts/AuthContext';
import { handleActivation } from 'apis/accessoryApi';
import { useNavigate } from 'react-router-dom';
import avatarPlaceholder from '../../images/avatar-cropped.png';

const AccessoryActivate = ({
  onActivationSuccess,
  userCards,
  accessoryData,
  serialNumber
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [error, setError] = useState('');
  const { authState } = useAuth();
  const navigate = useNavigate();

  const handleActivate = async () => {
    setError('');

    if (!selectedCardId || !accessoryData) {
      setError(!selectedCardId ? 'Please select a card' : 'Accessory data not found');
      return;
    }

    setLoading(true);
    try {
      if (!authState?.user?.userId) {
        throw new Error('Please sign in to activate this accessory');
      }

      const result = await handleActivation(
        accessoryData.serialNumber,
        authState.user.userId,
        selectedCardId
      );

      if (result.success) {
        onActivationSuccess(selectedCardId);
      } else {
        throw new Error('Failed to activate device');
      }
    } catch (error) {
      setError(error.message || 'Failed to activate device. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#fff',
        padding: { xs: '16px', sm: '20px' }
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '440px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#000',
            marginBottom: '32px',
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}
        >
          Activate Device
        </Typography>

        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '320px' }, marginBottom: '32px' }}>
          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: "#4f4f4f",
                marginBottom: '8px'
              }}
            >
              Serial Number
            </Typography>
            <FormControl
              fullWidth
              sx={{
                backgroundColor: '#f7f7f7',
                borderRadius: '8px'
              }}
            >
              <Select
                value={serialNumber}
                disabled
                IconComponent={() => null}
                sx={{ height: '48px' }}
              >
                <MenuItem value={serialNumber}>
                  <Typography sx={{ fontFamily: 'monospace', color: '#666' }}>
                    {serialNumber}
                  </Typography>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: "#4f4f4f",
                marginBottom: '8px'
              }}
            >
              Select Card
            </Typography>
            <FormControl
              fullWidth
              sx={{
                backgroundColor: '#f7f7f7',
                borderRadius: '8px'
              }}
            >
              <Select
                value={selectedCardId}
                onChange={(e) => {
                  setSelectedCardId(e.target.value);
                  setError('');
                }}
                displayEmpty
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  height: '48px',
                  borderRadius: '12px',
                  '& .MuiSelect-select': {
                    borderRadius: '12px',
                    padding: '10px 14px',
                  },
                  '& fieldset': {
                    border: 'none',
                    borderRadius: '12px',
                  }
                }}
                error={!!error && !selectedCardId}
              >
                <MenuItem value="" disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar
                      src={avatarPlaceholder}
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 2
                      }}
                    />
                    <Typography sx={{ color: '#999', fontSize: '0.875rem' }}>
                      Select a card
                    </Typography>
                  </Box>
                </MenuItem>
                {userCards.map((card) => (
                  <MenuItem key={card._id} value={card._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Avatar
                        src={card.profilePictureUrl || avatarPlaceholder}
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 2
                        }}
                      />
                      <Typography sx={{ fontSize: '0.875rem' }}>
                        {card.contact_info?.fullName || 'Unnamed Card'}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {error && (
          <Typography
            sx={{
              color: 'error.main',
              fontSize: '0.8rem',
              fontWeight: 500,
              marginBottom: 2,
              textAlign: 'center',
              width: '100%',
              maxWidth: '320px'
            }}
          >
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          onClick={handleActivate}
          disabled={loading || !selectedCardId}
          fullWidth
          sx={{
            maxWidth: { xs: '100%', sm: '320px' },
            backgroundColor: '#000',
            color: '#fff',
            fontWeight: '500',
            borderRadius: '8px',
            padding: { xs: '14px', sm: '12px' },
            textTransform: 'none',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            '&:hover': {
              backgroundColor: '#333',
            }
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Activate this device'}
        </Button>
      </Box>
    </Box>
  );
};

export default AccessoryActivate;